// FI-2181: wires utils/build_watcher.js to the router, timers and window.
// kept apart from the pure module so that one stays testable in mocha.
// called once at the end of router/index.js, so it runs for every mount
// of the app, the chromeless standalone boards included (they bypass
// App.vue's authenticated branch but not the router).
//
// no visibilitychange handler on purpose: some chromium contexts fire it
// at ~1 Hz (see App.vue mounted). setInterval and router hooks only.
import {
  CURRENT_BUILD, CHECK_INTERVAL_MS, FIRST_CHECK_DELAY_MS, BOARD_JITTER_MAX_MS, RELOAD_LOOP_BACKOFF_MS,
  state, hasNewBuild, isCypress, fetchBuildId, noteFetchedBuild, reloadLoopDetected, readReloadAttempt,
  clearReloadAttempt, reloadOnNavigateDecision, displayReloadDue, isDisplayBoardRoute, isEditableElement, checkDue,
  isAnyDirty, isReloading, reloadInto, sessionStorageOf,
} from './build_watcher'
import { isChromelessPath } from './chromeless_routes'
import { fsEvent } from './fullstory'

export function installBuildWatcher(router, { publicRouteNames = [], win, now = Date.now } = {}) {
  const w = win || (typeof window !== 'undefined' ? window : null)
  // a local serve has no build id; the e2e suite stubs requests it would
  // lose on a reload
  if (!w || !CURRENT_BUILD || isCypress(w)) return null

  const pageLoadedAt = now()
  // one jitter per tab so a wall of display boards does not blink together
  const jitterMs = Math.floor(Math.random() * BOARD_JITTER_MAX_MS)
  let lastInputAt = null
  let lastCheckAt = 0
  let backoffUntil = 0
  // null where storage is blocked: this runs while the router module loads,
  // so a throw here would leave every page blank (FI-2266)
  const storage = sessionStorageOf(w)
  let attemptedBuild = readReloadAttempt(storage, CURRENT_BUILD)

  // display idle rule: passive, timestamps only
  const onInput = () => { lastInputAt = now() }
  w.addEventListener('pointerdown', onInput, { passive: true })
  w.addEventListener('keydown', onInput, { passive: true })

  function currentRoute() {
    return (router && router.currentRoute) || { fullPath: '/', path: '/' }
  }

  function onDisplayRoute() {
    const route = currentRoute()
    const hashPath = String(w.location.hash || '').replace(/^#/, '')
    // chromeless forms (public erm/sos forms, register, sos dispatcher) are
    // not boards: they never self-reload
    return isDisplayBoardRoute({
      chromeless: isChromelessPath(route.fullPath, hashPath, route.path),
      routeName: route.name,
      publicRouteNames,
    })
  }

  function maybeDisplayReload() {
    // unsaved work wins here too (a chromed board with a note half typed),
    // and so does a focused field: a keystroke may still be a while off
    if (isReloading() || isAnyDirty()) return
    if (isEditableElement(w.document && w.document.activeElement)) return
    const due = displayReloadDue({
      newBuild: hasNewBuild(),
      isDisplay: onDisplayRoute(),
      newBuildSeenAt: state.newBuildSeenAt,
      lastInputAt,
      pageLoadedAt,
      nowMs: now(),
      jitterMs,
    })
    if (!due) return
    fsEvent('build_refresh', { mode: 'board' })
    reloadInto(currentRoute().fullPath, w)
  }

  async function check() {
    const t = now()
    if (t < backoffUntil || isReloading()) return
    lastCheckAt = t
    const fetched = await fetchBuildId()
    if (!fetched) return
    if (reloadLoopDetected({ attemptedBuild, currentBuild: CURRENT_BUILD, fetchedBuild: fetched })) {
      // the last reload in this tab did not land on the new build (a
      // cached shell, a deploy still settling): hold off for an hour
      // rather than reload again and again
      backoffUntil = now() + RELOAD_LOOP_BACKOFF_MS
      attemptedBuild = null
      clearReloadAttempt(storage)
      // eslint-disable-next-line no-console
      console.warn('build watcher: reload did not pick up build ' + fetched + ', backing off 60 min')
      return
    }
    noteFetchedBuild({ current: CURRENT_BUILD, fetched, nowMs: now() })
    maybeDisplayReload()
  }

  const firstTimer = setTimeout(() => {
    check()
    setInterval(check, CHECK_INTERVAL_MS)
  }, FIRST_CHECK_DELAY_MS)

  // registered AFTER the role guard in router/index.js so a navigation the
  // guard redirects (next({name: 'dashboard'})) never reaches this hook.
  // in-component beforeRouteLeave guards (ops_desk.vue, erm_board.vue,
  // tenant_config_shell.vue) run before any global beforeEach in
  // vue-router 3, so an unsaved-changes prompt still wins.
  router.beforeEach((to, from, next) => {
    // once a reload is under way let everything through: the hashchange
    // our hash write raises must not be bounced back to the old route
    if (isReloading()) { next(); return }
    const reload = reloadOnNavigateDecision({
      newBuild: hasNewBuild(),
      dirty: isAnyDirty(),
      isCypress: isCypress(w),
      toName: to.name,
      toPath: to.path,
      fromPath: from.path,
      publicRouteNames,
    })
    if (!reload) { next(); return }
    next(false)
    fsEvent('build_refresh', { mode: 'navigation' })
    reloadInto(to.fullPath, w)
  })

  // a navigation is a cheap moment to refresh what we know, so the NEXT
  // one can act on it; throttled so a flurry of clicks costs one request
  router.afterEach(() => {
    if (checkDue({ lastCheckAt, nowMs: now() })) check()
  })

  return { check, firstTimer }
}
